import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, Card, message, Modal } from 'antd';
import { UserOutlined, LockOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { get, post, setMerchantToken, setMerchantApps, getMerchantToken } from '../../utils/merchantApi';
import CryptoJS from 'crypto-js';

const GA_LENGTH = 6;

const MerchantLogin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [gaModalVisible, setGaModalVisible] = useState(false);
  const [loginTicket, setLoginTicket] = useState('');
  const [gaAccount, setGaAccount] = useState('');
  const [gaCodeArr, setGaCodeArr] = useState(Array(GA_LENGTH).fill(''));
  const [gaSubmitting, setGaSubmitting] = useState(false);
  const gaInputRefs = useRef([]);

  useEffect(() => {
    const token = getMerchantToken();
    if (token) {
      navigate('/merchant');
    }
  }, [navigate]);

  // 弹框打开时清空并聚焦第一格
  useEffect(() => {
    if (gaModalVisible) {
      setGaCodeArr(Array(GA_LENGTH).fill(''));
      setTimeout(() => gaInputRefs.current[0]?.focus(), 100);
    }
  }, [gaModalVisible]);

  // 6位填满自动提交
  useEffect(() => {
    if (gaModalVisible && gaCodeArr.every((d) => /^\d$/.test(d)) && !loading && !gaSubmitting) {
      handleGaSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gaCodeArr.join('')]);

  const encryptPassword = (password) => {
    return CryptoJS.SHA256(password).toString();
  };

  const handleLoginSuccess = async (token) => {
    setMerchantToken(token);

    const appsResponse = await get('/merchant/apps');
    if (appsResponse.data?.length > 0) {
      setMerchantApps(appsResponse.data);
      if (appsResponse.data.length === 1) {
        localStorage.setItem('currentAppId', appsResponse.data[0].app_id);
      }
    } else {
      message.error('您尚未绑定商户应用');
      return;
    }

    message.success('登录成功');
    navigate('/merchant');
  };

  const handleLogin = async (values) => {
    setLoading(true);
    try {
      const response = await post('/app/auth/login', {
        loginType: 'password',
        phone: values.phone,
        password: encryptPassword(values.password)
      });

      if (response.data?.needGa) {
        setLoginTicket(response.data.loginTicket);
        setGaAccount(response.data.account);
        setGaModalVisible(true);
        return;
      }

      if (response.data?.token) {
        await handleLoginSuccess(response.data.token);
      }
    } catch (error) {
      console.error('登录失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 单格输入
  const handleGaInputChange = (index, value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length > 1) {
      // 粘贴场景：分发到各格
      const next = Array(GA_LENGTH).fill('');
      digits.split('').forEach((ch, i) => {
        if (i < GA_LENGTH) next[i] = ch;
      });
      setGaCodeArr(next);
      gaInputRefs.current[Math.min(digits.length, GA_LENGTH) - 1]?.focus();
      return;
    }
    setGaCodeArr((prev) => {
      const next = [...prev];
      next[index] = digits;
      return next;
    });
    if (digits && index < GA_LENGTH - 1) {
      gaInputRefs.current[index + 1]?.focus();
    }
  };

  // 退格回退
  const handleGaKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !gaCodeArr[index] && index > 0) {
      e.preventDefault();
      setGaCodeArr((prev) => {
        const next = [...prev];
        next[index - 1] = '';
        return next;
      });
      gaInputRefs.current[index - 1]?.focus();
    }
  };

  // 粘贴整串
  const handleGaPaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text');
    const digits = text.replace(/\D/g, '');
    if (digits) {
      handleGaInputChange(0, digits);
    }
  };

  // 提交二次验证
  const handleGaSubmit = async () => {
    const gaCode = gaCodeArr.join('');
    if (!/^\d{6}$/.test(gaCode)) {
      message.warning('请输入完整的6位动态验证码');
      return;
    }
    setGaSubmitting(true);
    try {
      const response = await post('/app/auth/login/ga', {
        loginTicket,
        gaCode
      });

      if (response.data?.token) {
        setGaModalVisible(false);
        await handleLoginSuccess(response.data.token);
      }
    } catch (error) {
      console.error('Google验证码校验失败:', error);
      setGaCodeArr(Array(GA_LENGTH).fill(''));
      setTimeout(() => gaInputRefs.current[0]?.focus(), 100);
    } finally {
      setGaSubmitting(false);
    }
  };

  const handleCloseGaModal = () => {
    setGaModalVisible(false);
    setLoginTicket('');
    setGaAccount('');
    setGaCodeArr(Array(GA_LENGTH).fill(''));
    form.resetFields();
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <Card
        style={{
          width: '100%',
          maxWidth: 400,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          borderRadius: '12px'
        }}
        bodyStyle={{ padding: '40px 32px' }}
      >
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <h1 style={{ 
            fontSize: '28px', 
            fontWeight: '600', 
            color: '#262626',
            marginBottom: '8px'
          }}>
            商户管理后台
          </h1>
          <p style={{ color: '#8c8c8c', fontSize: '14px' }}>
            商户登录
          </p>
        </div>

        <Form
          form={form}
          name="merchantLogin"
          onFinish={handleLogin}
          autoComplete="off"
          size="large"
        >
          <Form.Item
            name="phone"
            rules={[
              { required: true, message: '请输入手机号' }
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="手机号"
              style={{ borderRadius: '8px' }}
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[
              { required: true, message: '请输入密码' },
              { min: 6, message: '密码至少6位' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="密码"
              style={{ borderRadius: '8px' }}
            />
          </Form.Item>

          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              block
              style={{ 
                borderRadius: '8px',
                height: '44px',
                fontSize: '16px',
                fontWeight: '500'
              }}
            >
              登录
            </Button>
          </Form.Item>

          <div style={{ textAlign: 'center' }}>
            <Button type="link" onClick={() => navigate('/login')}>
              返回管理员登录
            </Button>
          </div>
        </Form>
      </Card>

      <Modal
        title={null}
        open={gaModalVisible}
        onCancel={handleCloseGaModal}
        footer={null}
        width={420}
        closable={false}
        maskClosable={false}
      >
        <div style={{ textAlign: 'center', padding: '24px 8px 8px' }}>
          <div style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            margin: '0 auto 16px',
            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <SafetyCertificateOutlined style={{ fontSize: 30, color: '#fff' }} />
          </div>
          <h2 style={{ fontSize: 20, fontWeight: 600, color: '#262626', marginBottom: 8 }}>
            二次验证
          </h2>
          <p style={{ color: '#8c8c8c', fontSize: 13, marginBottom: 24 }}>
            {gaAccount ? `账号 ${gaAccount} ` : ''}已开启 Google 验证器，请输入 6 位动态验证码
          </p>

          <div
            onPaste={handleGaPaste}
            style={{ display: 'flex', gap: 10, justifyContent: 'center' }}
          >
            {gaCodeArr.map((digit, i) => (
              <Input
                key={i}
                ref={(el) => (gaInputRefs.current[i] = el)}
                value={digit}
                maxLength={6}
                inputMode="numeric"
                onChange={(e) => handleGaInputChange(i, e.target.value)}
                onKeyDown={(e) => handleGaKeyDown(i, e)}
                style={{
                  width: 46,
                  height: 54,
                  textAlign: 'center',
                  fontSize: 22,
                  fontWeight: 600,
                  borderRadius: 10,
                  borderColor: digit ? '#11998e' : '#d9d9d9',
                  boxShadow: digit ? '0 0 0 2px rgba(17, 153, 142, 0.15)' : 'none',
                  transition: 'all 0.2s'
                }}
              />
            ))}
          </div>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 28 }}>
            <Button
              size="large"
              onClick={handleCloseGaModal}
              style={{ borderRadius: 8, minWidth: 110 }}
            >
              取消
            </Button>
            <Button
              type="primary"
              size="large"
              loading={gaSubmitting || loading}
              onClick={handleGaSubmit}
              style={{
                borderRadius: 8,
                minWidth: 110,
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                border: 'none'
              }}
            >
              确认
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default MerchantLogin;
